/**
 * CAOS-372 — the "My Profile" app: the signed-in person's own corner of the workspace.
 *
 * Everything about YOU used to be scattered across reserved routes nobody could find twice — your
 * account at `/profile`, what you had reported at `/my-feedback` — reachable only from the avatar
 * menu, and named in the app switcher nowhere at all. The reference makes it a first-class app that
 * sits second from the bottom of the switcher, with administration under it, and this is that.
 *
 * It is a CONSOLE, the same shape Setup is: a left rail of sections beside one content pane. That is
 * deliberate — the two are the same KIND of place (settings that belong to someone), so they read the
 * same way and share the same chrome rather than each inventing a layout.
 *
 * Its sections, in the reference's own order:
 *   • Profile           — who you are, and the session you are signed in on.
 *   • Sign-in Activity   — your own recent sign-in attempts (CAOS-436 — see below).
 *   • Preferences        — how the workspace looks to you, and a way back to the defaults.
 *   • My Feedback        — what you reported, and what happened to it (a separate "Feedback" group).
 *
 * ── Sign-in Activity (CAOS-436) ───────────────────────────────────────────────────────────────────
 * CAOS-373 left this section out because the platform had no self-scoped read of your own sign-ins.
 * CAOS-478 shipped that read (`GET auth/sign-in-history`, no params ⇒ the caller's own history, never
 * silently narrowed to someone else's on a named request). This section is the first one of the four
 * authored as a real declared metadata PAGE (`sign_in_activity`, caos-pkg-core-shell) rather than as
 * host code: it is fetched and drawn by {@link renderPageSurface} (CAOS-744's controlPlane-backed
 * capability) exactly the way a Setup-contributed page already is (`setup-surface.ts`'s
 * `renderMain`), never a second hand-coded description of the same screen. Profile/Preferences stay
 * host code for now — converting them is a separate, later pass, not required to ship this section.
 */
import type { CaosTreeNode } from '@caos/ui';
import { renderProfileSurface, type EdgeTabChoice, type ProfileCard } from './profile.js';
import { renderRosterDestination } from './roster-destination.js';
import { pageNotice } from './page-surface.js';
import { renderPage } from '../content-source.js';
import { renderContributedPreferences, type ContributedPreference } from './contributed-preferences.js';
import type { OrgMetadata } from '../metadata-source.js';
import './setup-surface.css';

/** The `sign_in_activity` page's key (caos-pkg-core-shell/src/pages/sign_in_activity.json). */
export const SIGN_IN_ACTIVITY_PAGE_KEY = 'sign_in_activity';

/**
 * CAOS-1764 — the my_feedback_list roster's key
 * (caos-pkg-report-a-bug/src/rosterPages/my_feedback_list.json, rooted in that package's manifest).
 *
 * A constant here that must agree with a declaration in another repository, for the same reason
 * SIGN_IN_ACTIVITY_PAGE_KEY above is one: the host has to name the destination because the host
 * is what routes to it. The generated standard registry carries both declarations, which is where a
 * test can hold this against the real thing rather than against a second copy of the string.
 */
export const MY_FEEDBACK_ROSTER_KEY = 'my_feedback_list';

/** The client-side navigate seam (same one every console link routes through). */
export type Navigate = (href: string) => void;

/** The app's key — and so the first segment of every route it owns. */
export const MY_PROFILE_APP_KEY = 'my_profile';
export const MY_PROFILE_PATH = `/${MY_PROFILE_APP_KEY}`;

interface ProfileSection {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  /** The profile cards this section draws; absent ⇒ the section renders something else entirely. */
  readonly cards?: readonly ProfileCard[];
}

/**
 * The rail, in groups. Profile is the DEFAULT section: bare `/my_profile` lands on it, exactly as
 * bare `/setup` lands on Setup Home.
 */
const SECTION_GROUPS: readonly { readonly heading: string; readonly sections: readonly ProfileSection[] }[] = [
  {
    heading: 'My Profile',
    sections: [
      // CAOS-378 — a HOUSE. This is My Profile's Home: bare `/my_profile` lands here and the rail
      // heading leads back to it, exactly as Setup Home does. It carried a person, which is the mark
      // of the APP (the heading below draws it), so the same repetition Setup Home had.
      { key: '', label: 'Profile', icon: 'house', cards: ['account', 'session'] },
      // CAOS-436 — no `cards`: this section draws the declared `sign_in_activity` PAGE
      // (renderPageSurface), not a profile card. See the module doc above.
      { key: 'signin-activity', label: 'Sign-in Activity', icon: 'file-clock' },
      { key: 'preferences', label: 'Preferences', icon: 'settings', cards: ['appearance', 'edge-tabs', 'personalization'] },
    ],
  },
  {
    heading: 'Feedback',
    // CAOS-378 — a FLAG, which is what `caos-pkg-report-a-bug` already declares for this exact
    // destination in its own `my_feedback` tab. It was drawn here as an inbox, which is the mark the
    // reference gives Setup's *Submitted Feedback* — the administrator's view of everybody's
    // submissions. One destination drawn two ways was the near-miss; a flag is the thing a person
    // raised, an inbox is where it lands for somebody else.
    sections: [{ key: 'feedback', label: 'My Feedback', icon: 'flag' }],
  },
];

const ALL_SECTIONS = SECTION_GROUPS.flatMap((g) => g.sections);

/**
 * CAOS-397 — the rail's section list reshaped into `<caos-tree>` nodes, the same way Setup's is.
 * Data, computed once; the tree component turns it into rows.
 */
const RAIL_NODES: CaosTreeNode[] = SECTION_GROUPS.map((group) => ({
  id: group.heading,
  label: group.heading,
  children: group.sections.map((section) => ({
    id: section.key,
    label: section.label,
    icon: section.icon,
    href: section.key ? `${MY_PROFILE_PATH}/${section.key}` : MY_PROFILE_PATH,
  })),
}));

/** Is this a route the My Profile app owns? */
export function isMyProfilePath(pathname: string): boolean {
  return pathname === MY_PROFILE_PATH || pathname.startsWith(`${MY_PROFILE_PATH}/`);
}

/** The section a route names. An unknown section falls to Profile rather than to a blank pane. */
export function parseMyProfileRoute(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean); // ['my_profile', section?]
  const asked = segments[1] ?? '';
  return ALL_SECTIONS.some((s) => s.key === asked) ? asked : '';
}

/** The left rail — the console's persistent section navigation. */
function renderRail(activeKey: string, navigate?: Navigate): HTMLElement {
  const rail = document.createElement('nav');
  rail.className = 'setup-rail';
  rail.setAttribute('aria-label', 'My Profile');

  // CAOS-592 — one placed component for the whole rail: the heading (which doubles as the way back
  // to the default section, exactly as Setup's does) and the section list. RAIL_NODES stays the data.
  const tree = document.createElement('caos-tree');
  tree.setAttribute('selected-id', activeKey);
  // CAOS-378 — `user-round`, the mark `caos-pkg-core-shell` declares for the my_profile APP, so the
  // rail heading and the app switcher draw the same thing for the same app. It was `user`, a
  // different person glyph, which is the drift this ticket exists to close.
  tree.heading = { label: 'My Profile', icon: 'user-round', href: MY_PROFILE_PATH };
  tree.nodes = RAIL_NODES;
  tree.addEventListener('tree-choose', (ev) => {
    const { href } = (ev as CustomEvent<{ id: string; href?: string }>).detail;
    if (href) navigate?.(href);
  });
  rail.append(tree);
  return rail;
}

export interface MyProfileContext {
  readonly pathname: string;
  readonly navigate?: Navigate;
  readonly userName?: string;
  /**
   * CAOS-1809 — the viewer as a placed component's inputs name them, for the `sign_in_activity`
   * PAGE this surface draws: a piece placed on it declaring `source: "userContext"` is answered from
   * here. Distinct from `userName` above on purpose — that is this surface's own display string,
   * this is the keyed record `viewerInputContext` produces, and only the caller may build it.
   */
  readonly viewer?: Readonly<Record<string, string>>;
  readonly fetchImpl?: typeof fetch;
  /**
   * CAOS-436 — the org's installed metadata: how the Sign-in Activity section resolves and draws its
   * `sign_in_activity` PAGE. Required (unlike `userName`/`fetchImpl`) because it is not a display
   * nicety — without it the section has no page to look up and says so honestly (see
   * {@link renderSignInActivityPane}) rather than silently rendering nothing.
   */
  readonly meta: OrgMetadata;
  /**
   * CAOS-749 — the settings installed packages contribute to Preferences, read off the shell frame
   * plan by `contributedPreferences`. Absent ⇒ none, which is what every workspace showed before
   * this seam existed; the section then draws exactly the cards it always did.
   */
  readonly contributedPreferences?: readonly ContributedPreference[];
  /**
   * CAOS-751 — the edge-anchored sources installed for this org, resolved by the caller from the
   * same shell plan the gutter itself is built from. Absent ⇒ the Preferences section draws no edge
   * tab switches at all, which is what a caller with no plan in hand (a test of another section,
   * the reserved `/profile` route before the frame exists) should get rather than an empty list
   * claiming nothing is installed.
   *
   * Distinct from {@link contributedPreferences} on purpose: that is a setting a package DECLARES
   * for itself, this is the workspace's own answer about a seam it renders. Every anchored source
   * gets a switch here, including one whose package never thought to declare anything — which is
   * what "each installed edge-anchored source" asks for, and what a per-package opt-in could not
   * promise.
   */
  readonly edgeTabs?: readonly EdgeTabChoice[];
}

/**
 * CAOS-436 — the Sign-in Activity pane: the declared `sign_in_activity` PAGE (caos-pkg-core-shell),
 * drawn by the SAME generic renderer a Setup-contributed page already uses
 * (`setup-surface.ts`'s `renderMain`, `renderPageSurface` from `page-surface.ts`) — no second,
 * hand-coded description of this screen. `renderPageSurface` draws its OWN header (the page's own
 * `label`), so this pane carries none of its own — two headings stacked is how a page looks
 * unfinished (see the console's own test, "the section heading is drawn once").
 */
function renderSignInActivityPane(ctx: MyProfileContext): HTMLElement {
  const pane = document.createElement('div');
  pane.className = 'setup-pane';
  const page = ctx.meta.pages.get(SIGN_IN_ACTIVITY_PAGE_KEY);
  if (!page) {
    pane.append(
      pageNotice(
        '“Sign-in Activity” is not installed here',
        'This workspace does not have the “Sign-in Activity” page. Install the package that provides it.',
      ),
    );
    return pane;
  }
  // CAOS-1809 — threaded from the caller, not assembled from `ctx.userName` beside it. This surface
  // holds the display name already, so building the keyed record here would work and would also put
  // the `name`-to-`userName` spelling in a second place; `viewerInputContext` is the only thing that
  // may know it, and `main.ts` calls it once.
  pane.append(
    renderPage(page, {
      meta: ctx.meta,
      appLabel: 'My Profile',
      ...(ctx.viewer ? { viewer: ctx.viewer } : {}),
    }),
  );
  return pane;
}

/**
 * CAOS-1764 — the My Feedback pane, drawn from the DECLARED roster rather than from host code.
 *
 * ── WHAT CHANGED AND WHY IT IS A DELETION RATHER THAN A REWRITE ─────────────────────────────────
 * This pane used to call `renderMyFeedbackSurface`, 225 lines of host code that built a list of the
 * reporter's own submissions: the columns, the reporter-worded status mapping, the `FB-` reference,
 * the date formatting. Every one of those decisions is ALSO declared, in
 * `caos-pkg-report-a-bug/src/rosterPages/my_feedback_list.json` — rooted in that package's manifest
 * as `rosterPage:my_feedback_list`, and reached today as the `feedback` app's landing tab. Two
 * implementations of one screen, and the metadata one is the richer: it carries status filter tabs
 * and sort options the host copy never had.
 *
 * So there was nothing to port. The declared roster is drawn through
 * {@link renderRosterDestination}, which is the seam built for exactly this — it resolves a roster
 * spec by key and hands it to the same renderers every other roster route uses, so nothing about
 * how this list draws is decided here. The host file is gone.
 *
 * ── AND THAT IS WHAT FIXES THE SPACING, which is the symptom the ticket was filed on ────────────
 * The complaint was content sitting edge-to-edge instead of taking the padding every other page
 * gets. The cause was being hand-rolled: `renderRosterDestination` returns a `setup-pane` root, the
 * same class the Sign-in Activity pane beside it wraps its declared page in, so the padding is
 * inherited rather than restated. This function therefore adds no wrapper of its own — a second
 * `setup-pane` around one would double the padding, which is the opposite error and just as
 * visible.
 *
 * ── THE EYEBROW IS THE ROUTE'S, NOT THE SPEC'S ──────────────────────────────────────────────────
 * CAOS-754 settled that this surface draws exactly ONE heading, and "My Profile" was half of it.
 * The roster spec cannot carry that line, because the same spec is the `feedback` app's landing tab
 * where "My Profile" would be false. The route supplies it, which is why
 * {@link renderRosterDestination} takes one.
 *
 * `pageNotice` is passed as the absent-destination renderer so a workspace without
 * `caos-pkg-report-a-bug` reads exactly as one without the Sign-in Activity page does — the same
 * sentence shape, from the same function, rather than a second wording of the same event.
 */
function renderFeedbackPane(ctx: MyProfileContext): HTMLElement {
  return renderRosterDestination('rosterPage', MY_FEEDBACK_ROSTER_KEY, 'My Feedback', {
    ...(ctx.navigate ? { navigate: ctx.navigate } : {}),
    ...(ctx.fetchImpl ? { fetch: ctx.fetchImpl } : {}),
    renderFallback: pageNotice,
    eyebrow: 'My Profile',
  }).root;
}

/** The content pane for one section. */
function renderPane(sectionKey: string, ctx: MyProfileContext): HTMLElement {
  if (sectionKey === 'signin-activity') return renderSignInActivityPane(ctx);
  if (sectionKey === 'feedback') return renderFeedbackPane(ctx);

  const pane = document.createElement('div');
  pane.className = 'setup-pane';

  const section = ALL_SECTIONS.find((s) => s.key === sectionKey) ?? ALL_SECTIONS[0]!;

  const header = document.createElement('header');
  header.className = 'caos-frame__page-header';
  const eyebrow = document.createElement('div');
  eyebrow.className = 'caos-frame__page-eyebrow';
  eyebrow.textContent = 'My Profile';
  const title = document.createElement('h1');
  title.className = 'caos-frame__page-title';
  title.textContent = section.label;
  header.append(eyebrow, title);
  pane.append(header);

  // Every section reaching this point (Profile, Preferences) declares `cards` — Sign-in Activity and
  // Feedback are handled above, before this generic header is ever built.
  pane.append(
    renderProfileSurface(
      {
        cards: section.cards!,
        heading: false,
        ...(ctx.userName ? { userName: ctx.userName } : {}),
        ...(ctx.edgeTabs ? { edgeTabs: ctx.edgeTabs } : {}),
      },
      ctx.fetchImpl ?? fetch,
    ),
  );

  // CAOS-749 — and, on Preferences, whatever the installed packages contribute. It goes after the
  // platform's own cards rather than among them: these are settings this workspace happens to have
  // because of what is installed, and a person reading down the page should meet what is always
  // there first. A workspace where nothing is contributed draws nothing extra at all.
  if (sectionKey === 'preferences') {
    const contributed = renderContributedPreferences(ctx.contributedPreferences ?? []);
    if (contributed) pane.append(contributed);
  }
  return pane;
}

/** The My Profile console: the rail + the section pane. */
export function renderMyProfileSurface(ctx: MyProfileContext): HTMLElement {
  const console_ = document.createElement('div');
  console_.className = 'setup-console';
  const sectionKey = parseMyProfileRoute(ctx.pathname);
  console_.append(renderRail(sectionKey, ctx.navigate), renderPane(sectionKey, ctx));
  return console_;
}
